import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/feature/ComicCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import CategoryHeader from "@/pages/category/components/CategoryHeader";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { CATEGORIES, COMICS, REGIONS, YEARS } from "@/mocks/comics";
import { filterComics, getCategoryByKey, paginate, type FilterOptions } from "@/lib/comics";
import { usePageMeta, useScrollTop } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;
const STATUS_LIST = ["全部", "连载中", "已完结"];

export default function CategoryList() {
  const { type } = useParams();
  const category = getCategoryByKey(type);
  const [filters, setFilters] = useState<FilterOptions>({ status: "全部", region: "全部", year: "全部", sort: "latest" });
  const [page, setPage] = useState(1);

  const categoryName = category?.name || "";
  const baseList = useMemo(
    () => (category ? COMICS.filter((item) => item.categories.includes(category.name)) : []),
    [category],
  );

  const result = useMemo(() => filterComics(baseList, filters), [baseList, filters]);
  const paged = useMemo(() => paginate(result, page, PAGE_SIZE), [result, page]);

  useScrollTop(`${type}-${page}`);
  usePageMeta(
    category
      ? `禁漫天堂 - ${categoryName}漫画资源`
      : "禁漫天堂 - 分类不存在",
    category
      ? `${categoryName}漫画资源合集，${category.desc}禁漫天堂整理最新上线的${categoryName}作品，支持按连载状态、地区、更新年份与最新最热排序筛选，快速找到想读的漫画。`
      : "该分类页面不存在，请返回首页或浏览其他漫画分类。",
    category
      ? `禁漫天堂,${categoryName}漫画,${categoryName}资源,漫画下载,${category.en}`
      : "禁漫天堂,漫画分类",
  );

  if (!category) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-16">
          <EmptyState
            title="该分类不存在"
            description="我们找不到这个分类，可能是链接有误。你可以回到首页，或从热门分类中继续浏览。"
            suggestions={[
              { label: "返回首页", to: "/" },
              { label: "热血漫画", to: "/category/hot-blood" },
              { label: "奇幻漫画", to: "/category/fantasy" },
            ]}
          />
        </div>
      </SiteLayout>
    );
  }

  const updateFilters = (patch: Partial<FilterOptions>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ status: "全部", region: "全部", year: "全部", sort: "latest" });
    setPage(1);
  };

  return (
    <SiteLayout>
      <CategoryHeader categoryName={categoryName} description={category.desc} count={baseList.length} />

      <div className="w-full px-4 md:px-6 mt-6">
        <div className="flex flex-col lg:flex-row gap-5 lg:gap-6">
          <aside className="lg:w-[200px] shrink-0">
            <div className="rounded-lg border hairline bg-background-900/60 p-3">
              <p className="px-1 pb-2 text-[11px] font-label tracking-[0.18em] text-foreground-500">全部题材</p>
              <ul className="space-y-1">
                {CATEGORIES.map((cat) => (
                  <li key={cat.key}>
                    <Link
                      to={`/category/${cat.key}`}
                      className={`flex items-center gap-2 h-9 px-2.5 rounded-md text-xs transition-colors ${
                        cat.key === type
                          ? "bg-primary-500/20 text-accent-300 border border-primary-500/40"
                          : "text-foreground-300 hover:bg-background-800/70 hover:text-foreground-50 border border-transparent"
                      }`}
                    >
                      <i className={`${cat.icon} text-accent-400`} />
                      <span className="whitespace-nowrap">{cat.name}漫画</span>
                      <span className="ml-auto text-[10px] text-foreground-500">
                        {COMICS.filter((c) => c.categories.includes(cat.name)).length}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <FilterPanel
              options={filters}
              onChange={updateFilters}
              onReset={resetFilters}
              statusList={STATUS_LIST}
              regionList={["全部", ...REGIONS]}
              yearList={YEARS}
            />

            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-foreground-500">
                当前筛选命中 <span className="text-accent-300 font-label">{result.length}</span> 部作品
              </p>
              <p className="text-xs text-foreground-500">
                第 {paged.page} / {paged.totalPages} 页
              </p>
            </div>

            <div className="mt-3">
              {paged.items.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                  {paged.items.map((comic, i) => (
                    <div key={comic.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                      <ComicCard comic={comic} />
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState onReset={resetFilters} resetLabel="重置筛选" />
              )}
            </div>

            <Pagination page={paged.page} totalPages={paged.totalPages} onPage={setPage} />
          </div>
        </div>
      </div>
      <div className="h-12" />
    </SiteLayout>
  );
}